import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, CalendarCheck2 } from "lucide-react";
import TrialDatePicker from "@/components/site/TrialDatePicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIME_SLOTS = ["Morning", "Afternoon", "Either"];

export default function Reschedule() {
  const { leadId } = useParams();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/leads/${leadId}/public`);
        if (cancelled) return;
        setLead(data);
        setDate(data.preferred_trial_date || "");
        setSlot(data.trial_time_slot || "");
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) setNotFound(true);
        else toast.error("Couldn't load your booking. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leadId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot) {
      toast.error("Pick a new date and time slot.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/leads/${leadId}/reschedule`, {
        preferred_trial_date: date,
        trial_time_slot: slot,
      });
      setLead(data);
      setDone(true);
      toast.success("Booking updated. We'll be in touch to confirm.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      let msg;
      if (status === 429) {
        msg = "Too many reschedule attempts. Please try again in an hour.";
      } else if (status === 404) {
        setNotFound(true);
        msg = "This booking link is no longer valid.";
      } else {
        msg = Array.isArray(detail) ? detail[0]?.msg : (detail || "Couldn't update your date. Please try again.");
      }
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main data-testid="reschedule-page" className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-2xl px-6 py-16 md:py-24">
        <Link
          to="/"
          data-testid="reschedule-home-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground hover:text-primary transition-colors"
        >
          ← Back to Overlander Mobility
        </Link>

        <div className="mt-10">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
            Reschedule your trial
          </span>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight font-bold leading-[1.05]">
            Pick a new date <br /> that suits you.
          </h1>
        </div>

        {loading ? (
          <p data-testid="reschedule-loading" className="mt-10 text-sm text-muted-foreground">Loading your booking…</p>
        ) : notFound ? (
          <div data-testid="reschedule-not-found" className="mt-10 border border-border rounded-sm p-8 bg-muted">
            <p className="font-display text-xl font-bold uppercase tracking-tight">
              Booking not found.
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              This link is either expired or was mistyped. Head back to the site and start a fresh trial request — takes a minute.
            </p>
            <Button asChild variant="outline" className="mt-6 rounded-sm uppercase tracking-wider font-bold">
              <Link to="/#test-drive" data-testid="reschedule-restart-link">Book a trial</Link>
            </Button>
          </div>
        ) : done ? (
          <div
            data-testid="reschedule-success"
            className="mt-10 bg-background text-foreground rounded-sm p-10 border border-primary"
          >
            <CalendarCheck2 className="h-10 w-10 text-primary" />
            <h2 className="mt-4 font-display text-2xl md:text-3xl font-bold uppercase tracking-tight">
              Locked in.
            </h2>
            <p className="mt-3 text-muted-foreground">
              Your new preferred date is <strong>{lead?.preferred_trial_date}</strong>
              {lead?.trial_time_slot ? <> · <strong>{lead.trial_time_slot}</strong></> : null}.
              A specialist will call to confirm the exact time.
            </p>
            <Button asChild variant="outline" className="mt-6 rounded-sm uppercase tracking-wider font-bold">
              <Link to="/" data-testid="reschedule-back-home">Back to site</Link>
            </Button>
          </div>
        ) : (
          <form
            data-testid="reschedule-form"
            onSubmit={submit}
            className="mt-10 border border-border rounded-sm p-7 md:p-10 space-y-5 bg-background"
          >
            <p className="text-sm text-muted-foreground">
              Hi {lead?.first_name || "there"} — pick a new date and time slot below. We&apos;ll update your booking and let your specialist know.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                  New preferred date <span className="text-primary">*</span>
                </Label>
                <TrialDatePicker
                  testId="reschedule-input-date"
                  value={date}
                  onChange={setDate}
                  state={lead?.state}
                  placeholder="Pick a weekday"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                  Time slot <span className="text-primary">*</span>
                </Label>
                <Select value={slot} onValueChange={setSlot}>
                  <SelectTrigger data-testid="reschedule-select-slot" className="rounded-sm">
                    <SelectValue placeholder="Morning or afternoon?" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`reschedule-slot-${s.toLowerCase()}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              data-testid="reschedule-submit"
              type="submit"
              disabled={saving}
              className="w-full rounded-sm uppercase tracking-wider font-bold py-6 text-sm group"
            >
              {saving ? "Updating…" : "Update my booking"}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>

            <p className="text-[11px] text-muted-foreground leading-relaxed">
              You picked <strong>{lead?.preferred_trial_date || "—"}</strong>
              {lead?.trial_time_slot ? <> · {lead.trial_time_slot}</> : null} originally.
            </p>
          </form>
        )}
      </div>
    </main>
  );
}
